import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { X, UserCheck } from 'lucide-react';
import { AppModal, AppButton, AppTextField, AppSelect, useToast } from '@/components';
import { adminClientsApi, adminTagsApi } from '@/api';
import { colors, spacing, radius } from '@/theme/tokens';
import type { ClientTagDTO } from '@tilly/shared';

/** Debounce a fast-changing value so the match-on-create search doesn't fire
 * on every keystroke. 300ms lands well under typing-pause perception. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

interface NewClientModalProps {
  businessId: string;
  open: boolean;
  onClose: () => void;
}

export function NewClientModal({ businessId, open, onClose }: NewClientModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState<'' | 'en-US' | 'es-MX'>('');
  const [notes, setNotes] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [addTagValue, setAddTagValue] = useState('');
  const [dismissedMatches, setDismissedMatches] = useState(false);

  const tagsQuery = useQuery({
    queryKey: ['tags', businessId],
    queryFn: () => adminTagsApi.getTags(businessId),
    enabled: !!businessId && open,
  });

  // Match-on-create: surface existing clients that look like the same person
  // before creating a duplicate. Losing the old phone-based dedupe guarantee
  // (phone is now optional) means this is the main defense against split
  // history/loyalty across two client records for the same person.
  const debouncedName = useDebouncedValue(name.trim(), 300);
  const matchQuery = useQuery({
    queryKey: ['client-matches', businessId, debouncedName],
    queryFn: () => adminClientsApi.getClients(businessId, debouncedName),
    enabled: !!businessId && open && debouncedName.length >= 2 && !dismissedMatches,
    staleTime: 15_000,
  });
  const matches = dismissedMatches ? [] : (matchQuery.data?.clients ?? []);

  const reset = () => {
    setName('');
    setPhone('');
    setEmail('');
    setPreferredLanguage('');
    setNotes('');
    setTagIds([]);
    setAddTagValue('');
    setDismissedMatches(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const createMutation = useMutation({
    mutationFn: () =>
      adminClientsApi.createClient(businessId, {
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        preferredLanguage: preferredLanguage || undefined,
        notes: notes.trim() || undefined,
        tagIds,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['clients', businessId] });
      const client = result.client as { id: string };
      if (!result.isNew) {
        showSuccess(t('admin:clients.newClient.toasts.alreadyExists'));
        handleClose();
        navigate(`/admin/clients/${client.id}`);
        return;
      }
      showSuccess(t('admin:clients.newClient.toasts.created'));
      handleClose();
    },
    onError: (err: Error) => showError(err.message),
  });

  const tags = tagsQuery.data?.tags || [];
  const valid = name.trim().length > 0;

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title={t('admin:clients.newClient.title')}
      size="sm"
      actions={
        <>
          <AppButton variant="ghost" onClick={handleClose} disabled={createMutation.isPending}>
            {t('common:buttons.cancel')}
          </AppButton>
          <AppButton
            onClick={() => createMutation.mutate()}
            disabled={!valid || createMutation.isPending}
            loading={createMutation.isPending}
          >
            {t('common:buttons.save')}
          </AppButton>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        <AppTextField
          label={t('admin:clients.newClient.name')}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setDismissedMatches(false);
          }}
          fullWidth
        />

        {matches.length > 0 && (
          <div
            data-testid="client-match-suggestions"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[2],
              padding: spacing[3],
              borderRadius: radius.md,
              backgroundColor: colors.surfaceRaised,
            }}
          >
            <p style={{ fontSize: '13px', color: colors.textMuted }}>
              {t('admin:clients.newClient.possibleMatches')}
            </p>
            {matches.slice(0, 3).map((match) => (
              <button
                key={match.id}
                data-testid="client-match-row"
                onClick={() => {
                  handleClose();
                  navigate(`/admin/clients/${match.id}`);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: spacing[2],
                  padding: spacing[2],
                  borderRadius: radius.sm,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.surface,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
                  <UserCheck size={14} style={{ color: colors.textMuted }} />
                  <span style={{ fontSize: '14px', color: colors.textPrimary }}>
                    {match.name}
                    {match.phone ? ` — ${match.phone}` : ''}
                  </span>
                </span>
                <span style={{ fontSize: '13px', color: colors.accent }}>
                  {t('admin:clients.newClient.useThisClient')}
                </span>
              </button>
            ))}
            <button
              onClick={() => setDismissedMatches(true)}
              style={{
                alignSelf: 'flex-start',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontSize: '13px',
                color: colors.textMuted,
                textDecoration: 'underline',
              }}
            >
              {t('admin:clients.newClient.notAMatch')}
            </button>
          </div>
        )}

        <AppTextField
          label={t('admin:clients.newClient.phone')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
        />
        <AppTextField
          label={t('admin:clients.newClient.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
        />
        <AppSelect
          fullWidth
          label={t('admin:clients.newClient.language')}
          value={preferredLanguage}
          onChange={(e) => setPreferredLanguage(e.target.value as '' | 'en-US' | 'es-MX')}
          options={[
            { value: '', label: t('admin:clients.newClient.languageUnset') },
            { value: 'en-US', label: t('admin:clients.newClient.languageEnUs') },
            { value: 'es-MX', label: t('admin:clients.newClient.languageEsMx') },
          ]}
        />
        <AppTextField
          label={t('admin:clients.newClient.notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />

        {tags.length > 0 && (
          <div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: spacing[2],
                marginBottom: spacing[2],
              }}
            >
              {tags
                .filter((tag: ClientTagDTO) => tagIds.includes(tag.id))
                .map((tag: ClientTagDTO) => (
                  <div
                    key={tag.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: spacing[1],
                      padding: `${spacing[1]} ${spacing[2]}`,
                      borderRadius: radius.full,
                      border: `1px solid ${tag.color}`,
                      backgroundColor: `${tag.color}22`,
                      fontSize: '13px',
                      fontWeight: 500,
                      color: colors.textPrimary,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: radius.full,
                        backgroundColor: tag.color,
                        flexShrink: 0,
                      }}
                    />
                    {tag.name}
                    <button
                      onClick={() => setTagIds(tagIds.filter((id) => id !== tag.id))}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        color: colors.textMuted,
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
            </div>
            {tags.filter((tag: ClientTagDTO) => !tagIds.includes(tag.id)).length > 0 && (
              <AppSelect
                fullWidth
                label={t('admin:clients.newClient.addTag')}
                value={addTagValue}
                onChange={(e) => {
                  const val = e.target.value as string;
                  if (val && !tagIds.includes(val)) setTagIds([...tagIds, val]);
                  setAddTagValue('');
                }}
                options={[
                  { value: '', label: t('admin:clients.newClient.selectTag') },
                  ...tags
                    .filter((tag: ClientTagDTO) => !tagIds.includes(tag.id))
                    .map((tag: ClientTagDTO) => ({ value: tag.id, label: tag.name })),
                ]}
              />
            )}
          </div>
        )}
      </div>
    </AppModal>
  );
}
